import {
  useEffect,
  useMemo,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import { useRouter } from "next/navigation";
import type { CollabPreview } from "@cabal/shared/domain/session/collab/types.js";
import type { ReflectionPreview } from "@cabal/shared/domain/session/reflection/types.js";
import type {
  ProductDetailResponse,
  ProductSummary,
} from "@cabal/shared/domain/session/product/types.js";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import type { SessionOverviewResponse } from "@cabal/shared/domain/session/types.js";
import type { UserShortIdView } from "@cabal/shared/domain/session/user-short-id/types.js";
import type {
  VersionDetailResponse,
  VersionPreview,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import { useApi } from "@/contexts/api";
import { LogLevel, useSeqLog } from "@/hooks/telemetry";
import { isForbiddenApiError } from "@/utils/session-mutation-errors";
import { resolveUnreachableRouteDestination } from "@/lib/authReturnRoute";
import type {
  LoadingStates,
  PollTimestamps,
  ProductsSeenMap,
} from "@/types/frontend-types";
import type { SessionCollab, SessionTeamDetails } from "./context";
import {
  toMembersMap,
  toProductMapFromInitialLoad,
  toRecordById,
} from "./initial-load-selectors";
import {
  selectInitialProductsSeen,
} from "./read-tracking-selectors";
import {
  CHAT_PAGE_LIMIT,
  PollingSetupSource,
  SessionUpdateSource,
  getPollingIntervals,
  isNotFoundError,
  isUnauthorizedError,
  selectInitialChatState,
  type PollingIntervals,
} from "./session-data-provider-utils";

type SessionDataApi = ReturnType<typeof useApi>;
type SessionByIdApi = ReturnType<SessionDataApi["sessions"]["byId"]>;
type SessionInitialLoadData = Awaited<
  ReturnType<SessionByIdApi["initialLoad"]>
>;
type SessionPreviewsData = Awaited<ReturnType<SessionByIdApi["previews"]>>;
type SessionProviderLogger = ReturnType<typeof useSeqLog>;

interface InitialLoadSnapshot {
  data: SessionInitialLoadData;
  previews: SessionPreviewsData | null;
}

interface SessionInitialLoadLifecycle {
  cancelled: boolean;
}

interface UseSessionInitialLoadInput {
  api: SessionDataApi;
  router: ReturnType<typeof useRouter>;
  sessionId: string;
  authLoading: boolean;
  userId: string | null;
  sessionGenerationRef: MutableRefObject<number>;
  rootProductIdRef: MutableRefObject<string | null>;
  timestamps: MutableRefObject<PollTimestamps>;
  haltStatusDebugLog: SessionProviderLogger;
  applySessionFromSource: (input: {
    nextSession: SessionOverviewResponse;
    source: SessionUpdateSource;
  }) => void;
  restoreInitialNavigation: (input: {
    productsMap: Record<string, ProductSummary>;
    rootProductId: string | null;
  }) => void;
  startPolling: (input: {
    intervals: PollingIntervals;
    source: PollingSetupSource;
  }) => void;
  clearPolling: () => void;
  cleanupLiveSignalTimers: () => void;
  resetLiveSignals: () => void;
  resetReadTracking: () => void;
  resetTimestamps: () => void;
  showUnauthorizedSessionModal: () => void;
  openInitialLoadGate: () => void;
  mergeProductReadItems: (input: {
    items: SessionInitialLoadData["productReads"];
    replace: boolean;
  }) => void;
  replaceProductsSeen: (input: { productsSeen: ProductsSeenMap }) => void;
  setSessionLoading: Dispatch<SetStateAction<LoadingStates>>;
  setNotFound: Dispatch<SetStateAction<boolean>>;
  setTeamDefaultStartingRounds: Dispatch<SetStateAction<number | null>>;
  setTeamDetails: Dispatch<SetStateAction<SessionTeamDetails | null>>;
  setUrlRootProductId: Dispatch<SetStateAction<string | null>>;
  setProducts: Dispatch<SetStateAction<Record<string, ProductSummary>>>;
  setVersions: Dispatch<SetStateAction<Record<string, VersionSummary>>>;
  setProductDetailsById: Dispatch<
    SetStateAction<Record<string, ProductDetailResponse>>
  >;
  setVersionDetailsById: Dispatch<
    SetStateAction<Record<string, VersionDetailResponse>>
  >;
  setMembers: Dispatch<SetStateAction<Record<string, Member>>>;
  setChatMessages: Dispatch<SetStateAction<Record<string, SessionCollab>>>;
  setCollabs: Dispatch<SetStateAction<Record<string, SessionCollab>>>;
  setVersionPreviews: Dispatch<SetStateAction<Record<string, VersionPreview>>>;
  setCollabPreviews: Dispatch<SetStateAction<Record<string, CollabPreview>>>;
  setReflectionPreviews: Dispatch<
    SetStateAction<Record<string, ReflectionPreview>>
  >;
  setUserShortIdViews: Dispatch<SetStateAction<UserShortIdView[]>>;
  setChatMessageIds: Dispatch<SetStateAction<string[]>>;
  setChatCursor: Dispatch<SetStateAction<string | null>>;
  setChatHasMore: Dispatch<SetStateAction<boolean>>;
  setChatLoadingMore: Dispatch<SetStateAction<boolean>>;
  setCollabsByProduct: Dispatch<SetStateAction<Record<string, string[]>>>;
}

type InitialLoadCoreInput = Pick<
  UseSessionInitialLoadInput,
  | "api"
  | "router"
  | "sessionId"
  | "authLoading"
  | "userId"
  | "sessionGenerationRef"
  | "rootProductIdRef"
  | "timestamps"
  | "haltStatusDebugLog"
>;

type InitialLoadActionInput = Pick<
  UseSessionInitialLoadInput,
  | "applySessionFromSource"
  | "restoreInitialNavigation"
  | "startPolling"
  | "clearPolling"
  | "cleanupLiveSignalTimers"
  | "resetLiveSignals"
  | "resetReadTracking"
  | "resetTimestamps"
  | "showUnauthorizedSessionModal"
  | "openInitialLoadGate"
  | "mergeProductReadItems"
  | "replaceProductsSeen"
>;

type InitialLoadSessionSetterInput = Pick<
  UseSessionInitialLoadInput,
  | "setSessionLoading"
  | "setNotFound"
  | "setTeamDefaultStartingRounds"
  | "setTeamDetails"
  | "setUrlRootProductId"
  | "setProducts"
  | "setVersions"
  | "setProductDetailsById"
  | "setVersionDetailsById"
  | "setMembers"
>;

type InitialLoadFeedSetterInput = Pick<
  UseSessionInitialLoadInput,
  | "setChatMessages"
  | "setCollabs"
  | "setVersionPreviews"
  | "setCollabPreviews"
  | "setReflectionPreviews"
  | "setUserShortIdViews"
  | "setChatMessageIds"
  | "setChatCursor"
  | "setChatHasMore"
  | "setChatLoadingMore"
  | "setCollabsByProduct"
>;

const SESSION_GENERATION_BUMP_REASON_BIG_EFFECT_TEARDOWN =
  "big-effect-teardown";

const INITIAL_LOADING_STATE: LoadingStates = {
  session: true,
  products: true,
  members: true,
  chat: true,
};

const READY_LOADING_STATE: LoadingStates = {
  session: false,
  products: false,
  members: false,
  chat: false,
};

export function useSessionInitialLoad(
  input: UseSessionInitialLoadInput,
): void {
  const effectInput = useSessionInitialLoadEffectInput(input);

  useEffect(() => runSessionInitialLoadEffect(effectInput), [effectInput]);
}

function useSessionInitialLoadEffectInput(
  input: UseSessionInitialLoadInput,
): UseSessionInitialLoadInput {
  const core = useInitialLoadCoreInput(input);
  const actions = useInitialLoadActionInput(input);
  const sessionSetters = useInitialLoadSessionSetters(input);
  const feedSetters = useInitialLoadFeedSetters(input);

  return useMemo(
    () => ({
      ...core,
      ...actions,
      ...sessionSetters,
      ...feedSetters,
    }),
    [actions, core, feedSetters, sessionSetters],
  );
}

function useInitialLoadCoreInput({
  api,
  router,
  sessionId,
  authLoading,
  userId,
  sessionGenerationRef,
  rootProductIdRef,
  timestamps,
  haltStatusDebugLog,
}: UseSessionInitialLoadInput): InitialLoadCoreInput {
  return useMemo(
    () => ({
      api,
      router,
      sessionId,
      authLoading,
      userId,
      sessionGenerationRef,
      rootProductIdRef,
      timestamps,
      haltStatusDebugLog,
    }),
    [
      api,
      authLoading,
      haltStatusDebugLog,
      rootProductIdRef,
      router,
      sessionGenerationRef,
      sessionId,
      timestamps,
      userId,
    ],
  );
}

function useInitialLoadActionInput({
  applySessionFromSource,
  restoreInitialNavigation,
  startPolling,
  clearPolling,
  cleanupLiveSignalTimers,
  resetLiveSignals,
  resetReadTracking,
  resetTimestamps,
  showUnauthorizedSessionModal,
  openInitialLoadGate,
  mergeProductReadItems,
  replaceProductsSeen,
}: UseSessionInitialLoadInput): InitialLoadActionInput {
  return useMemo(
    () => ({
      applySessionFromSource,
      restoreInitialNavigation,
      startPolling,
      clearPolling,
      cleanupLiveSignalTimers,
      resetLiveSignals,
      resetReadTracking,
      resetTimestamps,
      showUnauthorizedSessionModal,
      openInitialLoadGate,
      mergeProductReadItems,
      replaceProductsSeen,
    }),
    [
      applySessionFromSource,
      cleanupLiveSignalTimers,
      clearPolling,
      mergeProductReadItems,
      openInitialLoadGate,
      replaceProductsSeen,
      resetLiveSignals,
      resetReadTracking,
      resetTimestamps,
      restoreInitialNavigation,
      showUnauthorizedSessionModal,
      startPolling,
    ],
  );
}

function useInitialLoadSessionSetters({
  setSessionLoading,
  setNotFound,
  setTeamDefaultStartingRounds,
  setTeamDetails,
  setUrlRootProductId,
  setProducts,
  setVersions,
  setProductDetailsById,
  setVersionDetailsById,
  setMembers,
}: UseSessionInitialLoadInput): InitialLoadSessionSetterInput {
  return useMemo(
    () => ({
      setSessionLoading,
      setNotFound,
      setTeamDefaultStartingRounds,
      setTeamDetails,
      setUrlRootProductId,
      setProducts,
      setVersions,
      setProductDetailsById,
      setVersionDetailsById,
      setMembers,
    }),
    [
      setMembers,
      setNotFound,
      setProductDetailsById,
      setProducts,
      setSessionLoading,
      setTeamDefaultStartingRounds,
      setTeamDetails,
      setUrlRootProductId,
      setVersionDetailsById,
      setVersions,
    ],
  );
}

function useInitialLoadFeedSetters({
  setChatMessages,
  setCollabs,
  setVersionPreviews,
  setCollabPreviews,
  setReflectionPreviews,
  setUserShortIdViews,
  setChatMessageIds,
  setChatCursor,
  setChatHasMore,
  setChatLoadingMore,
  setCollabsByProduct,
}: UseSessionInitialLoadInput): InitialLoadFeedSetterInput {
  return useMemo(
    () => ({
      setChatMessages,
      setCollabs,
      setVersionPreviews,
      setCollabPreviews,
      setReflectionPreviews,
      setUserShortIdViews,
      setChatMessageIds,
      setChatCursor,
      setChatHasMore,
      setChatLoadingMore,
      setCollabsByProduct,
    }),
    [
      setChatCursor,
      setChatHasMore,
      setChatLoadingMore,
      setChatMessageIds,
      setChatMessages,
      setCollabPreviews,
      setCollabs,
      setCollabsByProduct,
      setReflectionPreviews,
      setUserShortIdViews,
      setVersionPreviews,
    ],
  );
}

function runSessionInitialLoadEffect(
  input: UseSessionInitialLoadInput,
): (() => void) | void {
  input.haltStatusDebugLog(
    "big-effect: (re)run",
    {
      authLoading: input.authLoading,
      userId: input.userId,
      generation: input.sessionGenerationRef.current,
    },
    LogLevel.Info,
  );

  if (input.authLoading) {
    input.haltStatusDebugLog(
      "big-effect: EARLY-RETURN (authLoading) → polls stay down",
      { authLoading: input.authLoading, userId: input.userId },
      LogLevel.Warning,
    );
    return;
  }

  const lifecycle: SessionInitialLoadLifecycle = { cancelled: false };
  queueSessionScopedReset({ lifecycle, input });
  void runInitialLoad({ lifecycle, input });

  return () => {
    cleanupInitialLoad({ lifecycle, input });
  };
}

function queueSessionScopedReset(input: {
  lifecycle: SessionInitialLoadLifecycle;
  input: UseSessionInitialLoadInput;
}): void {
  queueMicrotask(() => {
    if (input.lifecycle.cancelled) {
      return;
    }

    resetSessionScopedState(input.input);
  });
}

function resetSessionScopedState(input: UseSessionInitialLoadInput): void {
  input.setSessionLoading(INITIAL_LOADING_STATE);
  input.resetLiveSignals();
  input.setChatMessages({});
  input.setChatMessageIds([]);
  input.setChatCursor(null);
  input.setChatHasMore(false);
  input.setChatLoadingMore(false);
  input.setCollabs({});
  input.setCollabsByProduct({});
  input.setProducts({});
  input.setVersions({});
  input.setProductDetailsById({});
  input.setVersionDetailsById({});
  input.setVersionPreviews({});
  input.setCollabPreviews({});
  input.setReflectionPreviews({});
  input.setMembers({});
  input.setUserShortIdViews([]);
  input.resetReadTracking();
  input.setTeamDefaultStartingRounds(null);
  input.setTeamDetails(null);
  input.rootProductIdRef.current = null;
  input.setUrlRootProductId(null);
  input.resetTimestamps();
}

async function runInitialLoad(input: {
  lifecycle: SessionInitialLoadLifecycle;
  input: UseSessionInitialLoadInput;
}): Promise<void> {
  try {
    const snapshot = await fetchInitialLoadSnapshot(input.input);
    if (input.lifecycle.cancelled) {
      return;
    }

    const productsMap = applyInitialLoadSnapshot({
      ...input.input,
      ...snapshot,
    });
    input.input.restoreInitialNavigation({
      productsMap,
      rootProductId: snapshot.data.session.rootProductId,
    });
    startInitialPolling({
      ...input.input,
      session: snapshot.data.session,
    });
  } catch (error) {
    handleInitialLoadError({ ...input.input, error });
  }
}

async function fetchInitialLoadSnapshot(
  input: UseSessionInitialLoadInput,
): Promise<InitialLoadSnapshot> {
  const sessionApi = input.api.sessions.byId(input.sessionId);
  const [data, previews] = await Promise.all([
    sessionApi.initialLoad({
      query: { chatLimit: CHAT_PAGE_LIMIT },
    }),
    sessionApi.previews().catch((): null => null),
  ]);

  return { data, previews };
}

function applyInitialLoadSnapshot(
  input: UseSessionInitialLoadInput & InitialLoadSnapshot,
): Record<string, ProductSummary> {
  input.applySessionFromSource({
    nextSession: input.data.session,
    source: SessionUpdateSource.InitialLoad,
  });
  applyInitialSessionMetadata(input);
  const membersMap = applyInitialMembers(input);
  const productsMap = applyInitialProducts({ ...input, membersMap });
  applyInitialChatAndReads(input);
  applyInitialPreviews(input);
  input.setSessionLoading(READY_LOADING_STATE);
  input.openInitialLoadGate();
  return productsMap;
}

function applyInitialSessionMetadata(
  input: UseSessionInitialLoadInput & InitialLoadSnapshot,
): void {
  input.setTeamDefaultStartingRounds(
    input.data.teamDefaultStartingRounds ?? null,
  );
  input.setTeamDetails(input.data.teamDetails ?? null);
  input.rootProductIdRef.current = input.data.session.rootProductId;
  input.setUrlRootProductId(input.data.session.rootProductId);
}

function applyInitialMembers(
  input: UseSessionInitialLoadInput & InitialLoadSnapshot,
): Record<string, Member> {
  const membersMap = toMembersMap(input.data.members);
  input.setMembers(membersMap);
  input.setUserShortIdViews(input.data.userShortIds);
  return membersMap;
}

function applyInitialProducts(
  input: UseSessionInitialLoadInput &
    InitialLoadSnapshot & { membersMap: Record<string, Member> },
): Record<string, ProductSummary> {
  const productsMap = toProductMapFromInitialLoad({
    products: input.data.products,
    membersById: input.membersMap,
  });
  input.setProducts(productsMap);
  input.replaceProductsSeen({
    productsSeen: selectInitialProductsSeen({ products: input.data.products }),
  });
  return productsMap;
}

function applyInitialChatAndReads(
  input: UseSessionInitialLoadInput & InitialLoadSnapshot,
): void {
  const { chatMap, chatIds } = selectInitialChatState(
    input.data.sessionCollabs,
  );
  input.setChatMessages(chatMap);
  input.setChatMessageIds(chatIds);
  input.setChatCursor(input.data.sessionCollabCursor);
  input.setChatHasMore(input.data.sessionCollabHasMore);
  input.setChatLoadingMore(false);
  input.mergeProductReadItems({
    items: input.data.productReads,
    replace: true,
  });
  input.timestamps.current.activitySince = input.data.timestamps.membersAt;
  input.timestamps.current.productsSince = input.data.timestamps.productsAt;
  input.timestamps.current.chatSince = input.data.timestamps.sessionCollabsAt;
  input.timestamps.current.productReadsSince =
    input.data.timestamps.productReadsAt;
}

function applyInitialPreviews(
  input: UseSessionInitialLoadInput & InitialLoadSnapshot,
): void {
  if (!input.previews) {
    return;
  }

  input.setVersionPreviews(toRecordById(input.previews.versions));
  input.setCollabPreviews(toRecordById(input.previews.collabs));
  input.setReflectionPreviews(toRecordById(input.previews.reflections));
}

function startInitialPolling(input: {
  session: SessionOverviewResponse;
} & UseSessionInitialLoadInput): void {
  if (!input.userId) {
    return;
  }

  const intervals = getPollingIntervals(input.session.status);
  input.haltStatusDebugLog(
    "poll-setup (initial effect)",
    {
      statusAtSetup: input.session.status,
      sessionIntervalMs: intervals.session,
      generation: input.sessionGenerationRef.current,
    },
    LogLevel.Info,
  );
  input.startPolling({
    intervals,
    source: PollingSetupSource.InitialEffect,
  });
}

function handleInitialLoadError(input: {
  error: unknown;
} & UseSessionInitialLoadInput): void {
  if (isUnauthorizedError(input.error)) {
    input.showUnauthorizedSessionModal();
  } else if (isNotFoundError(input.error)) {
    input.setNotFound(true);
  } else if (isForbiddenApiError(input.error)) {
    input.router.replace(resolveUnreachableRouteDestination("/forbidden"));
  }

  input.setSessionLoading(READY_LOADING_STATE);
}

function cleanupInitialLoad(input: {
  lifecycle: SessionInitialLoadLifecycle;
  input: UseSessionInitialLoadInput;
}): void {
  input.lifecycle.cancelled = true;
  input.input.haltStatusDebugLog(
    "generation-bump (big-effect teardown)",
    {
      from: input.input.sessionGenerationRef.current,
      to: input.input.sessionGenerationRef.current + 1,
      reason: SESSION_GENERATION_BUMP_REASON_BIG_EFFECT_TEARDOWN,
    },
    LogLevel.Warning,
  );
  input.input.sessionGenerationRef.current += 1;
  input.input.clearPolling();
  input.input.cleanupLiveSignalTimers();
}
